import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { ListTableColumn, ListTableRow } from 'framework/ui/ui-builder';
import { readColumnVisibilityOverrides, writeColumnVisibilityOverrides } from './storage';
import type { CollectionColumnsController, CollectionColumnsOptions, ColumnVisibilityOverrides } from './types';

const NO_OPTIONS: CollectionColumnsOptions = {};
const NO_IDS: string[] = [];

const getHiddenColumnIds = (defaultHiddenColumnIds: string[], overrides: ColumnVisibilityOverrides) => [
  ...defaultHiddenColumnIds.filter(id => !overrides.shown.includes(id)),
  ...overrides.hidden,
];

const getOverrides = (defaultHiddenColumnIds: string[], hiddenColumnIds: string[]): ColumnVisibilityOverrides => ({
  hidden: hiddenColumnIds.filter(id => !defaultHiddenColumnIds.includes(id)),
  shown: defaultHiddenColumnIds.filter(id => !hiddenColumnIds.includes(id)),
});

const sanitizeHiddenColumnIds = <Row extends ListTableRow>(
  hiddenColumnIds: string[],
  defaultHiddenColumnIds: string[],
  allColumns: ListTableColumn<Row>[],
) => {
  const isKnown = (id: string) => allColumns.some(column => column.id === id);
  const knownHiddenColumnIds = hiddenColumnIds.filter(isKnown);
  const knownDefaultHiddenColumnIds = defaultHiddenColumnIds.filter(isKnown);

  if (knownHiddenColumnIds.length < allColumns.length) return knownHiddenColumnIds;
  return knownDefaultHiddenColumnIds.length < allColumns.length ? knownDefaultHiddenColumnIds : [];
};

const useCollectionColumns = <Row extends ListTableRow>(
  allColumns: ListTableColumn<Row>[],
  { defaultHiddenColumnIds = NO_IDS, storageKey }: CollectionColumnsOptions = NO_OPTIONS,
): CollectionColumnsController<Row> => {
  const { pathname } = useLocation();
  const resolvedStorageKey = storageKey ?? pathname;
  const [overrides, setOverrides] = useState(() => readColumnVisibilityOverrides(resolvedStorageKey));

  useEffect(() => {
    setOverrides(readColumnVisibilityOverrides(resolvedStorageKey));
  }, [resolvedStorageKey]);

  const hiddenColumnIds = useMemo(
    () =>
      sanitizeHiddenColumnIds(
        getHiddenColumnIds(defaultHiddenColumnIds, overrides),
        defaultHiddenColumnIds,
        allColumns,
      ),
    [allColumns, defaultHiddenColumnIds, overrides],
  );

  const setHiddenColumnIds = useCallback(
    (nextHiddenColumnIds: string[]) => {
      const nextOverrides = getOverrides(defaultHiddenColumnIds, nextHiddenColumnIds);
      setOverrides(nextOverrides);
      writeColumnVisibilityOverrides(resolvedStorageKey, nextOverrides);
    },
    [defaultHiddenColumnIds, resolvedStorageKey],
  );

  const columns = useMemo(
    () => allColumns.filter(column => !hiddenColumnIds.includes(column.id)),
    [allColumns, hiddenColumnIds],
  );

  const columnsProps = useMemo(
    () => ({
      columns: allColumns.map(({ id, label }) => ({ id, label })),
      hiddenColumnIds,
      defaultHiddenColumnIds,
      onChange: setHiddenColumnIds,
    }),
    [allColumns, defaultHiddenColumnIds, hiddenColumnIds, setHiddenColumnIds],
  );

  return { columns, columnsProps };
};

export default useCollectionColumns;
